import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation, Link } from "react-router-dom";
import { api, prefetchGet } from "../lib/api";
import { Layout } from "../components/Layout";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatCard, SeverityBadge, ReviewBadge, Money } from "../components/badges";
import { num, fmtDate, sevRail } from "../lib/format";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import {
  ChevronRight, GitCompareArrows, UserPlus, UserMinus, Users, ArrowRightLeft,
  AlertTriangle, CheckCircle2, ListChecks,
} from "lucide-react";
import { MovementsTable } from "../components/MovementsTable";
import { EmployeesTable } from "../components/EmployeesTable";

const STATUS = {
  OPEN: { label: "Open", cls: "bg-neg/15 text-neg border-neg/30" },
  IN_REVIEW: { label: "In Review", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  COMPLETE: { label: "Complete", cls: "bg-pos/15 text-pos border-pos/30" },
};

const sevTile = (s) => (Number(s) >= 4 ? "border-neg/40 bg-neg/10 hover:bg-neg/15" : "border-border bg-card hover:bg-card-hover");

function TurnoverList({ testid, title, icon: Icon, tone, people, ccy }) {
  const toneCls = tone === "add"
    ? { rail: "border-l-pos", chip: "text-pos", bg: "bg-pos/[0.04]" }
    : { rail: "border-l-neg", chip: "text-neg", bg: "bg-neg/[0.04]" };
  return (
    <div data-testid={testid} className={`rounded-lg border border-border border-l-2 ${toneCls.rail} ${toneCls.bg} overflow-hidden`}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <Icon size={16} className={toneCls.chip} />
        <h3 className="font-heading text-sm font-bold uppercase tracking-wide">{title}</h3>
        <span className={`ml-auto font-mono text-sm font-bold ${toneCls.chip}`}>{num(people.length)}</span>
      </div>
      {people.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">None</div>
      ) : (
        <ul className="divide-y divide-border">
          {people.map((p) => (
            <li key={p.payroll_employee_GUID} className="flex items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{p.employee_name_as_printed}</div>
                <div className="text-xs text-muted-foreground truncate">{p.department_as_printed || "—"}</div>
                {(p.hire_date || p.termination_date) && (
                  <div className="text-xs font-medium mt-0.5">
                    <span className={toneCls.chip}>{p.hire_date ? "Hired" : "Termed"}</span>{" "}
                    <span className="font-mono text-muted-foreground">{p.hire_date || p.termination_date}</span>
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-sm"><Money value={p.gross_pay_amount} ccy={ccy} /></div>
                <div className="text-xs text-muted-foreground"><Money value={p.net_pay_amount} ccy={ccy} /> net</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function RunDetail() {
  const { runId } = useParams();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["run", runId],
    queryFn: async () => (await api.get(`/runs/${runId}`)).data,
  });
  const { data: mv } = useQuery({
    queryKey: ["run-movements", runId],
    queryFn: async () => (await api.get(`/runs/${runId}/movements`)).data,
  });
  const location = useLocation();
  useEffect(() => {
    if (location.hash === "#turnover" && data) {
      const el = document.getElementById("turnover");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location.hash, data]);

  if (isLoading)
    return (
      <Layout>
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-6 h-96 rounded-md" />
      </Layout>
    );

  if (error)
    return (
      <Layout>
        <div className="rounded-md border border-neg/30 bg-neg/5 p-6 text-neg">
          {error.response?.status === 403 ? "You do not have access to this customer." : "Failed to load payroll run."}
        </div>
      </Layout>
    );

  const { run, employees, run_findings, added_employees = [], dropped_employees = [], review_summary, priority_cards = [] } = data;
  const withIssues = employees.filter((e) => e.issue_count > 0).length;
  const rs = review_summary || { total: 0, explained: 0, open: 0 };
  const rsPct = rs.total > 0 ? Math.round((rs.explained / rs.total) * 100) : 0;
  const status = rs.total === 0 || rs.open === 0 ? "COMPLETE" : rs.explained > 0 ? "IN_REVIEW" : "OPEN";
  const st = STATUS[status];

  return (
    <Layout>
      <Breadcrumbs
        items={[
          { label: "Portfolio", to: "/" },
          { label: run.client_name, to: `/clients/${run.payroll_client_GUID}` },
          { label: `Register ${run.run_label}` },
        ]}
      />

      {/* Header */}
      <div className="mt-4 flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
        <div>
          <h1 className="font-heading text-4xl font-black">Register {run.run_label}</h1>
          <p className="mt-1 font-mono text-sm uppercase tracking-wide text-muted-foreground">
            {run.run_type_code} · Pay date {fmtDate(run.pay_date)}
            {run.pay_date_source && run.pay_date_source !== "PROVIDED" ? (
              <span
                data-testid="pay-date-source-badge"
                title="Pay date was not provided by the payroll system"
                className="ml-2 rounded border border-warn/40 bg-warn/10 px-1.5 py-0.5 text-[10px] font-bold text-warn"
              >
                {run.pay_date_source}
              </span>
            ) : null}
            {" · "}{fmtDate(run.period_start_date)} – {fmtDate(run.period_end_date)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {run.comparison && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm">
              <GitCompareArrows size={16} className="text-muted-foreground" />
              <span className="text-muted-foreground">vs</span>
              <span className="font-mono font-semibold">{run.comparison.comp_label}</span>
              <span className="text-muted-foreground">({fmtDate(run.comparison.comp_pay_date)})</span>
            </div>
          )}
          <span
            data-testid="run-status-badge"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider ${st.cls}`}
          >
            {status === "COMPLETE" ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {st.label}
          </span>
          <Button asChild size="sm" className="gap-1.5" data-testid="open-error-details-btn">
            <Link to={`/runs/${runId}/errors`}>Open Error Details <ChevronRight size={15} /></Link>
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard testid="run-employees" label="Employees" count={run.employee_count} bar="bg-foreground/30" />
        <StatCard testid="run-gross" label="Total Gross" value={<Money value={run.total_gross_amount} ccy={run.currency_code} />} bar="bg-sky-500/60" />
        <StatCard testid="run-net" label="Total Net" value={<Money value={run.total_net_amount} ccy={run.currency_code} />} bar="bg-emerald-500/60" />
        <StatCard testid="run-flagged" label="Employees Flagged" count={withIssues} accent={withIssues ? "text-neg" : "text-pos"} bar={withIssues ? "bg-neg" : "bg-pos"} />
        <StatCard testid="run-open-issues" label="Open Issues" count={rs.open} accent={rs.open ? "text-neg" : "text-pos"} bar={rs.open ? "bg-neg" : "bg-pos"} />
        <StatCard testid="run-reviewed" label="Reviewed" value={`${num(rs.explained)} / ${num(rs.total)}`} sub={`${rsPct}% cleared`} accent="text-pos" bar="bg-pos/60" />
      </div>

      {/* High-priority error tiles */}
      <section className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">High-Priority Items</h2>
          {priority_cards.length > 0 && (
            <Button asChild variant="outline" size="sm" data-testid="review-issues-btn">
              <Link to={`/runs/${runId}/errors`}>Review all errors <ChevronRight size={16} /></Link>
            </Button>
          )}
        </div>
        {priority_cards.length === 0 ? (
          <div className="flex items-center gap-2 rounded-lg border border-pos/30 bg-pos/5 p-6 text-pos">
            <CheckCircle2 size={18} /> No exceptions flagged for this register.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...priority_cards].sort((a, b) => (Number(b.severity) - Number(a.severity)) || (b.n - a.n)).map((p) => (
              <Link
                key={p.check_code}
                to={`/runs/${runId}/errors?check=${encodeURIComponent(p.check_code)}`}
                data-testid={`priority-card-${p.check_code}`}
                onMouseEnter={() => prefetchGet(qc, ["run-errors", runId], `/runs/${runId}/errors`)}
                className={`block rounded-lg border border-l-4 ${sevRail(p.severity)} ${sevTile(p.severity)} p-4 transition-colors`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-bold leading-tight">{p.check_name}</div>
                  <SeverityBadge severity={p.severity} />
                </div>
                <div className="mt-2 flex items-end justify-between">
                  <span className="font-mono text-3xl font-black">{num(p.n)}</span>
                  {p.impact ? <span className="font-mono text-xs text-muted-foreground">{run.currency_code} {num(p.impact, 0)}</span> : null}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{p.check_code}</div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Headcount turnover vs prior register */}
      {run.comparison && (
        <section className="mt-8 scroll-mt-24" id="turnover" data-testid="turnover-section">
          <div className="flex items-center gap-2 mb-3">
            <Users size={16} className="text-muted-foreground" />
            <h2 className="font-heading text-lg font-bold">Headcount Changes vs {run.comparison.comp_label}</h2>
          </div>
          {added_employees.length === 0 && dropped_employees.length === 0 ? (
            <div className="rounded-lg border border-border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
              No employees were added or dropped between these two registers.
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <TurnoverList testid="added-employees" title="Added" icon={UserPlus} tone="add" people={added_employees} ccy={run.currency_code} />
              <TurnoverList testid="dropped-employees" title="Dropped" icon={UserMinus} tone="drop" people={dropped_employees} ccy={run.currency_code} />
            </div>
          )}
        </section>
      )}

      {/* Run-level findings */}
      {run_findings?.length > 0 && (
        <section className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks size={16} className="text-muted-foreground" />
            <h2 className="font-heading text-lg font-bold">Register-Level Findings</h2>
          </div>
          <div className="space-y-2">
            {run_findings.map((f) => (
              <Link
                key={f.check_result_GUID}
                to={`/findings/${f.check_result_GUID}`}
                className={`flex items-center gap-3 rounded-lg border border-border border-l-4 ${sevRail(f.severity)} bg-card px-4 py-3 transition-all hover:border-foreground/30 hover:-translate-y-0.5 press`}
              >
                <SeverityBadge severity={f.severity} />
                <span className="font-medium">{f.check_name}</span>
                <span className="text-sm text-muted-foreground flex-1 truncate">{f.result_message}</span>
                <ReviewBadge status={f.review_status} />
                <ChevronRight size={16} className="text-muted-foreground" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Employees */}
      <section className="mt-8">
        <h2 className="font-heading text-lg font-bold mb-3">Employees in this Register</h2>
        <EmployeesTable employees={employees} ccy={run.currency_code} runId={runId} />
      </section>

      {/* What Moved — period-over-period compare grid */}
      <section className="mt-10" data-testid="run-movements-section">
        <div className="flex items-center gap-2 mb-3">
          <ArrowRightLeft size={16} className="text-muted-foreground" />
          <h2 className="font-heading text-lg font-bold">
            What Moved{mv?.previous_run_label ? ` vs ${mv.previous_run_label}` : ""}
          </h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Every reportable earning, deduction, tax and mileage movement between this register and the prior one.
        </p>
        <MovementsTable movements={mv?.movements || []} ccy={run.currency_code} showEmployee testid="run-movements" />
      </section>
    </Layout>
  );
}
